import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { updateLibraryItem } from '@/lib/api/generated/api';
import type { LibraryItemView } from '@/lib/api/generated/model';
import { invalidateWorkspaceFiles } from '@/features/workspace-files';
import { libraryKeys } from '../queryKeys';

export interface UpdateAllProgress {
  /** How many have been attempted, out of how many were asked for. */
  done: number;
  total: number;
  /** Keys that refused, with the service's reason. */
  refused: { key: string; reason: string }[];
}

/**
 * Update every outdated item in one press (spec 105, cycle 3).
 *
 * One request per item rather than a batch door, because the service already
 * has one and it already refuses the cases that must be refused — an item
 * changed here, and any workflow, which is not updated in place. A batch
 * endpoint would have to re-state all of that.
 *
 * Sequential on purpose. The requests write to the same workspace prefix and
 * the same record file, and the service writes that record whole; firing them
 * together would have them overwrite each other's entries. It is also the
 * order a reader can follow — "3 of 7" means something.
 *
 * A refusal stops nothing. The point of the button is to clear a list, so one
 * item that will not update is reported by name at the end rather than
 * abandoning the six that would have.
 */
export function useUpdateAll(workspaceId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<UpdateAllProgress | null>(null);
  const [running, setRunning] = useState(false);

  async function updateAll(items: readonly LibraryItemView[]) {
    if (running || items.length === 0) return;
    setRunning(true);
    const refused: UpdateAllProgress['refused'] = [];
    setProgress({ done: 0, total: items.length, refused });

    for (const [index, item] of items.entries()) {
      try {
        const response = await updateLibraryItem(workspaceId, {
          kind: item.kind,
          key: item.key,
        });
        if (response.status !== 200) {
          const data: unknown = response.data;
          const message =
            typeof data === 'object' &&
            data !== null &&
            typeof (data as { message?: unknown }).message === 'string'
              ? (data as { message: string }).message
              : 'It could not be updated.';
          refused.push({ key: item.key, reason: message });
        }
      } catch {
        refused.push({ key: item.key, reason: 'It could not be updated.' });
      }
      setProgress({
        done: index + 1,
        total: items.length,
        refused: [...refused],
      });
    }

    await queryClient.invalidateQueries({
      queryKey: libraryKeys.items(workspaceId),
    });
    await invalidateWorkspaceFiles(queryClient, workspaceId);
    setRunning(false);
  }

  return {
    updateAll: (items: readonly LibraryItemView[]) => void updateAll(items),
    running,
    progress,
    reset: () => {
      setProgress(null);
    },
  };
}
